import { useCallback, useEffect, useRef } from 'react';
import type { Dispatch, SetStateAction } from 'react';
import { useTranslation } from 'react-i18next';
import { Gauge, RefreshCw, Trophy } from 'lucide-react';
import { cn, isDevelopment } from '../../lib/utils';
import { formatDisplayDate, formatTimeDisplay } from '../../lib/gameUtils';
import { useDialogKeyboardNavigation } from '../../hooks/useDialogKeyboardNavigation';

interface DifficultyOption {
  value: number;
  translationKey: string;
}

const difficultyOptions: readonly DifficultyOption[] = [
  { value: 1, translationKey: 'easy' },
  { value: 2, translationKey: 'medium' },
  { value: 3, translationKey: 'hard' },
  { value: 4, translationKey: 'expert' }
];

interface SudokuDifficultySelectorProps {
  difficulty: number;
  isOpen: boolean;
  onToggle: () => void;
  onClose: () => void;
  onDifficultyChange: (difficulty: number) => boolean;
}

const SudokuDifficultySelector = ({
  difficulty,
  isOpen,
  onToggle,
  onClose,
  onDifficultyChange
}: SudokuDifficultySelectorProps) => {
  const { t } = useTranslation();
  const containerRef = useRef<HTMLDivElement>(null);
  const selectedOption = difficultyOptions.find(option => option.value === difficulty)!;

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    const handleOutsideClick = (event: MouseEvent) => {
      if (!containerRef.current?.contains(event.target as Node)) {
        onClose();
      }
    };

    containerRef.current
      ?.querySelector<HTMLButtonElement>(`[data-difficulty="${difficulty}"]`)
      ?.focus();
    document.addEventListener('click', handleOutsideClick);
    return () => document.removeEventListener('click', handleOutsideClick);
  }, [difficulty, isOpen, onClose]);

  const handleMenuKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    const buttons = Array.from(
      event.currentTarget.querySelectorAll<HTMLButtonElement>('button')
    );
    const focusedIndex = buttons.indexOf(document.activeElement as HTMLButtonElement);
    const currentIndex = focusedIndex >= 0 ? focusedIndex : 0;

    if (event.key === 'ArrowDown') {
      event.preventDefault();
      buttons[(currentIndex + 1) % buttons.length]?.focus();
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      buttons[(currentIndex - 1 + buttons.length) % buttons.length]?.focus();
    } else if (event.key === 'Escape') {
      event.preventDefault();
      onClose();
      document.getElementById('difficulty-button-sudoku')?.focus();
    }
  };

  const handleOptionClick = (newDifficulty: number) => {
    onClose();
    onDifficultyChange(newDifficulty);
  };

  return (
    <div ref={containerRef} className="relative flex items-center">
      <button
        id="difficulty-button-sudoku"
        className="min-h-11 rounded-lg transition-colors motion-reduce:transition-none disabled:cursor-not-allowed flex items-center space-x-1 sm:space-x-2 px-2 sm:px-4 py-1 sm:py-2 bg-cobalt text-white rounded-lg hover:bg-blue-800 text-xs sm:text-base whitespace-nowrap focus:outline-none focus:ring-2 focus:ring-indigo-500"
        onClick={onToggle}
        aria-haspopup="menu"
        aria-expanded={isOpen}
      >
        <Gauge className="h-4 w-4 sm:h-5 sm:w-5" />
        <span>{t(selectedOption.translationKey)}</span>
      </button>

      {isOpen && (
        <div
          id="difficulty-dropdown-sudoku"
          role="menu"
          onKeyDown={handleMenuKeyDown}
          className="absolute left-0 top-full mt-1 w-52 bg-white dark:bg-gray-800 rounded-md shadow-lg ring-1 ring-black ring-opacity-5 z-50"
        >
          <div className="bg-gray-100 dark:bg-gray-700 px-4 py-2 font-semibold text-gray-800 dark:text-gray-200 border-b border-gray-200 dark:border-gray-600 rounded-t-md">
            {t('difficulty')}
          </div>
          <div className="py-1">
            {difficultyOptions.map(option => (
              <button
                key={option.value}
                role="menuitem"
                data-difficulty={option.value}
                className={cn(
                  'min-h-11 block w-full text-left px-4 py-2 text-sm hover:bg-gray-100 dark:hover:bg-gray-700 focus:outline-none focus:bg-cobalt focus:text-white',
                  difficulty === option.value
                    ? 'bg-indigo-100 text-indigo-900 dark:bg-indigo-900 dark:text-indigo-100'
                    : 'text-gray-700 dark:text-gray-200'
                )}
                onClick={() => handleOptionClick(option.value)}
              >
                {t(option.translationKey)}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

interface SudokuPauseButtonProps {
  isPaused: boolean;
  isPuzzleCompleted: boolean;
  onPauseToggle: () => void;
}

const SudokuPauseButton = ({
  isPaused,
  isPuzzleCompleted,
  onPauseToggle
}: SudokuPauseButtonProps) => {
  const { t } = useTranslation();
  if (isPuzzleCompleted) {
    return null;
  }

  return (
    <button
      data-testid="button-pause-play"
      onClick={onPauseToggle}
      className={cn(
        'p-2 rounded-lg min-w-11 min-h-11 focus:outline-none focus:ring-2 focus:ring-indigo-500',
        isPaused
          ? 'bg-lagoon text-white hover:bg-teal-800'
          : 'bg-marigold text-midnight hover:bg-yellow-600'
      )}
      aria-label={isPaused ? t('play') : t('pause')}
    >
      {isPaused ? '▶' : '⏸'}
    </button>
  );
};

interface DevelopmentWinButtonProps {
  isPuzzleCompleted: boolean;
  isPreviewMode: boolean;
  onWin: () => void;
}

const DevelopmentWinButton = ({
  isPuzzleCompleted,
  isPreviewMode,
  onWin
}: DevelopmentWinButtonProps) => {
  const { t } = useTranslation();
  if (!isDevelopment() || isPuzzleCompleted || isPreviewMode) {
    return null;
  }

  return (
    <button
      onClick={onWin}
      className="min-h-11 rounded-lg transition-colors motion-reduce:transition-none disabled:cursor-not-allowed flex items-center space-x-1 sm:space-x-2 px-2 sm:px-4 py-1 sm:py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 text-xs sm:text-base whitespace-nowrap focus:outline-none focus:ring-2 focus:ring-indigo-500"
    >
      <Trophy className="h-4 w-4 sm:h-5 sm:w-5" />
      <span>{t('win')}</span>
    </button>
  );
};

interface SudokuGameHeaderProps {
  puzzleDate: Date;
  difficulty: number;
  elapsedTime: number;
  isPaused: boolean;
  isPuzzleCompleted: boolean;
  isPreviewMode: boolean;
  isDifficultyOpen: boolean;
  onToggleDifficulty: () => void;
  onCloseDifficulty: () => void;
  onDifficultyChange: (difficulty: number) => boolean;
  onPauseToggle: () => void;
  onReset: () => void;
  onWin: () => void;
}

export const SudokuGameHeader = ({
  puzzleDate,
  difficulty,
  elapsedTime,
  isPaused,
  isPuzzleCompleted,
  isPreviewMode,
  isDifficultyOpen,
  onToggleDifficulty,
  onCloseDifficulty,
  onDifficultyChange,
  onPauseToggle,
  onReset,
  onWin
}: SudokuGameHeaderProps) => {
  const { t, i18n } = useTranslation();

  return (
    <>
      <div className="text-center mb-4">
        <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-900 dark:text-white">
          {formatDisplayDate(puzzleDate, t, i18n, 'sudoku', false, isPreviewMode)}
        </h1>
      </div>
      <div className="flex flex-wrap items-center justify-between gap-2 mb-4">
        <div className="flex items-center justify-start flex-shrink overflow-visible">
          <SudokuDifficultySelector
            difficulty={difficulty}
            isOpen={isDifficultyOpen}
            onToggle={onToggleDifficulty}
            onClose={onCloseDifficulty}
            onDifficultyChange={onDifficultyChange}
          />
        </div>
        <div className="flex items-center justify-center space-x-2 flex-shrink-0">
          <div className="text-base sm:text-lg md:text-xl font-mono text-gray-700 dark:text-gray-300">
            {formatTimeDisplay(elapsedTime)}
          </div>
          <SudokuPauseButton
            isPaused={isPaused}
            isPuzzleCompleted={isPuzzleCompleted}
            onPauseToggle={onPauseToggle}
          />
        </div>
        <div className="flex items-center justify-end flex-shrink space-x-2">
          <DevelopmentWinButton
            isPuzzleCompleted={isPuzzleCompleted}
            isPreviewMode={isPreviewMode}
            onWin={onWin}
          />
          <button
            onClick={onReset}
            className="min-h-11 rounded-lg transition-colors motion-reduce:transition-none disabled:cursor-not-allowed flex items-center space-x-1 sm:space-x-2 px-2 sm:px-4 py-1 sm:py-2 bg-cobalt text-white rounded-lg hover:bg-blue-800 text-xs sm:text-base whitespace-nowrap focus:outline-none focus:ring-2 focus:ring-indigo-500"
          >
            <RefreshCw className="h-4 w-4 sm:h-5 sm:w-5" />
            <span>{t('reset')}</span>
          </button>
        </div>
      </div>
    </>
  );
};

interface SudokuInstructionsProps {
  showKeyboardControls: boolean;
  setShowKeyboardControls: Dispatch<SetStateAction<boolean>>;
}

export const SudokuInstructions = ({
  showKeyboardControls,
  setShowKeyboardControls
}: SudokuInstructionsProps) => {
  const { t } = useTranslation();

  return (
    <aside className="lg:w-80 space-y-8">
      <div className="bg-white dark:bg-slate-800 p-5 rounded-xl border border-slate-200 dark:border-slate-700">
        <h2 className="text-xl font-semibold mb-4 text-gray-900 dark:text-white">
          {t('howToPlay')}
        </h2>
        <ul className="space-y-3 text-gray-600 dark:text-gray-300">
          {(t('sudoku.instructions.points', { returnObjects: true }) as string[])
            .map((instruction, index) => (
              <li key={index}>{index + 1}. {instruction}</li>
            ))}
        </ul>
        <div className="mt-4 text-gray-600 dark:text-gray-300 font-medium">
          {t('sudoku.instructions.goodLuck')}
        </div>

        <div className="mt-4">
          <button
            onClick={() => setShowKeyboardControls(previous => !previous)}
            className="min-h-11 rounded-lg transition-colors motion-reduce:transition-none disabled:cursor-not-allowed flex items-center justify-between w-full text-left text-lg font-semibold text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
            aria-expanded={showKeyboardControls}
          >
            <span className="text-sm text-gray-500 dark:text-gray-400">
              {showKeyboardControls ? '▼' : '►'} {t('keyboardControls')}
            </span>
          </button>

          {showKeyboardControls && (
            <ul className="space-y-1 text-sm text-gray-600 dark:text-gray-300 mt-1 pl-2">
              <li><span className="font-semibold">1-9</span>: {t('enterNumber')}</li>
              <li><span className="font-semibold">0, Del, Backspace</span>: {t('clearCell')}</li>
              <li><span className="font-semibold">↑↓←→</span>: {t('moveCursor')}</li>
              <li><span className="font-semibold">{t('spaceKey')}</span>: {t('pauseResumeKey')}</li>
              <li><span className="font-semibold">P</span>: {t('togglePencilMode')}</li>
              <li><span className="font-semibold">A</span>: {t('toggleAutoPencil')}</li>
              <li><span className="font-semibold">C</span>: {t('toggleCoverageMode')}</li>
              <li><span className="font-semibold">H</span>: {t('hintKey')}</li>
              <li><span className="font-semibold">K</span>: {t('toggleKeyboardControls')}</li>
              <li><span className="font-semibold">Ctrl+R</span>: {t('resetKey')}</li>
              <li><span className="font-semibold">Ctrl+D</span>: {t('changeDifficultyKey')}</li>
            </ul>
          )}
        </div>
      </div>
    </aside>
  );
};

interface SudokuResetConfirmationDialogProps {
  isOpen: boolean;
  confirmationType: 'reset' | 'difficulty';
  onCancel: () => void;
  onConfirm: () => void;
}

export const SudokuResetConfirmationDialog = ({
  isOpen,
  confirmationType,
  onCancel,
  onConfirm
}: SudokuResetConfirmationDialogProps) => {
  const { t } = useTranslation();
  const handleEscape = useCallback(() => onCancel(), [onCancel]);
  const dialogRef = useDialogKeyboardNavigation(isOpen, handleEscape);
  const messageKey = confirmationType === 'reset'
    ? 'confirmReset'
    : 'confirmDifficultyChange';

  if (!isOpen) {
    return null;
  }

  return (
    <div
      ref={dialogRef}
      id="sudoku-reset-dialog"
      role="dialog"
      aria-modal="true"
      aria-describedby="sudoku-reset-message"
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
    >
      <div className="bg-white dark:bg-slate-800 p-5 rounded-xl border border-slate-200 dark:border-slate-700 max-w-sm w-full mx-4">
        <p id="sudoku-reset-message" className="text-gray-800 dark:text-gray-200 text-lg mb-4">
          {t(messageKey)}
        </p>
        <div className="flex justify-end gap-3">
          <button
            onClick={onCancel}
            className="min-h-11 rounded-lg transition-colors motion-reduce:transition-none disabled:cursor-not-allowed px-4 py-2 text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded focus:outline-none focus:ring-2 focus:ring-indigo-500"
          >
            {t('cancel')}
          </button>
          <button
            data-dialog-primary
            onClick={onConfirm}
            className="min-h-11 rounded-lg transition-colors motion-reduce:transition-none disabled:cursor-not-allowed px-4 py-2 bg-cobalt text-white rounded-lg hover:bg-blue-800 focus:outline-none focus:ring-2 focus:ring-indigo-500"
          >
            {t('confirm')}
          </button>
        </div>
      </div>
    </div>
  );
};
